import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePolling } from "@/lib/usePolling";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PlusCircle, Fuel, Gauge, Clock, CreditCard, Inbox, Pencil } from "lucide-react";
import { toast } from "sonner";
import ReceiptButton from "@/components/ReceiptButton";
import ReceiptUpload from "@/components/ReceiptUpload";

const fmtEur = (n) =>
  "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Rome" }) : "-";

export default function MyRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(0);
  const [editReq, setEditReq] = useState(null);
  const [editNote, setEditNote] = useState("");

  const load = () => api.get("/requests").then((r) => setItems(r.data));

  const doRequestEdit = async () => {
    try {
      await api.post(`/requests/${editReq.id}/request-edit`, { note: editNote });
      toast.success("Richiesta di modifica inviata");
      setEditReq(null);
      setEditNote("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load().finally(() => setLoading(false));
    api.get("/settings").then((r) => setThreshold(r.data.receipt_required_above || 0)).catch(() => {});
  }, []);

  usePolling(load, 15000);

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">
            Le mie richieste
          </h1>
          <p className="mt-1 text-sm text-slate-500">Storico e stato delle tue richieste.</p>
        </div>
        <Link to="/nuova-richiesta">
          <Button className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]" data-testid="new-request-btn">
            <PlusCircle className="mr-2 h-4 w-4" /> Nuova richiesta
          </Button>
        </Link>
      </div>

      {loading ? (
        <p className="text-slate-400">Caricamento...</p>
      ) : items.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-3 rounded-lg border-slate-200 py-16 text-center">
          <Inbox className="h-10 w-10 text-slate-300" />
          <p className="font-heading text-lg font-semibold text-slate-700">Nessuna richiesta</p>
          <p className="text-sm text-slate-500">Crea la tua prima richiesta di rifornimento.</p>
          <Link to="/nuova-richiesta">
            <Button className="mt-2 rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">
              <PlusCircle className="mr-2 h-4 w-4" /> Nuova richiesta
            </Button>
          </Link>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="my-requests-list">
          {items.map((r) => (
            <Card
              key={r.id}
              data-testid={`request-card-${r.id}`}
              className="rounded-lg border-slate-200 p-5 shadow-sm transition-transform duration-200 hover:-translate-y-[2px] hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-heading text-lg font-bold text-slate-900">
                    {r.vehicle_name}{" "}
                    {r.vehicle_plate && (
                      <span className="text-sm font-normal text-slate-400">{r.vehicle_plate}</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-400">{fmtDate(r.created_at)}</p>
                </div>
                <StatusBadge status={r.status} />
              </div>
              <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm text-slate-600">
                <span className="flex items-center gap-1.5">
                  {r.usage_type === "km" ? <Gauge className="h-4 w-4 text-slate-400" /> : <Clock className="h-4 w-4 text-slate-400" />}
                  {r.usage_value} {r.usage_type === "km" ? "km" : "ore"}
                </span>
                <span className="flex items-center gap-1.5">
                  <Fuel className="h-4 w-4 text-slate-400" /> {r.fuel_type}
                </span>
                {r.fuel_card && (
                  <span className="flex items-center gap-1.5">
                    <CreditCard className="h-4 w-4 text-slate-400" /> {r.fuel_card}
                  </span>
                )}
                <span className="ml-auto font-heading text-lg font-bold text-[#002FA7]">
                  {fmtEur(r.amount)}
                </span>
              </div>
              {r.notes && <p className="mt-3 text-sm text-slate-500">{r.notes}</p>}
              {r.status !== "in_attesa" && r.review_note && (
                <p className="mt-3 rounded-md bg-slate-50 p-2 text-sm text-slate-600">
                  <span className="font-medium">Nota del responsabile:</span> {r.review_note}
                </p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-3">
                {r.edit_requested ? (
                  <span data-testid={`edit-requested-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">Modifica richiesta — in attesa</span>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => { setEditReq(r); setEditNote(""); }} data-testid={`request-edit-btn-${r.id}`} className="rounded-md"><Pencil className="mr-2 h-4 w-4" />Richiedi modifica</Button>
                )}
              </div>
              {(r.receipt_path || r.status === "approvata") && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  {r.receipt_path ? (
                    <>
                      <ReceiptButton path={r.receipt_path} />
                      {r.actual_amount != null && (
                        <span className="text-sm text-slate-600" data-testid={`actual-amount-${r.id}`}>
                          Importo effettivo: <b className="text-slate-900">{fmtEur(r.actual_amount)}</b>
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <ReceiptUpload requestId={r.id} requestedAmount={r.amount} onDone={load} />
                      <span
                        className={`text-xs ${threshold > 0 && r.amount >= threshold ? "text-rose-600" : "text-slate-400"}`}
                        data-testid={`receipt-reminder-${r.id}`}
                      >
                        {threshold > 0 && r.amount >= threshold
                          ? "Scontrino obbligatorio: carica la foto dopo il rifornimento"
                          : "Ricordati di caricare la foto dello scontrino"}
                      </span>
                    </>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editReq} onOpenChange={(o) => !o && setEditReq(null)}>
        <DialogContent data-testid="my-request-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Richiedi modifica</DialogTitle>
            <DialogDescription>Invia una richiesta all'amministratore per modificare questa richiesta di rifornimento.</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="my-edit-note">Nota (facoltativa)</Label>
            <Textarea id="my-edit-note" data-testid="my-request-edit-note" value={editNote} onChange={(e) => setEditNote(e.target.value)} className="mt-1.5" placeholder="Es. importo errato, mezzo sbagliato..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditReq(null)}>Annulla</Button>
            <Button onClick={doRequestEdit} data-testid="my-request-edit-submit" className="bg-[#002FA7] text-white hover:bg-[#00227A]">Invia richiesta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
